import { FlexBox, Inline } from '@ardoq/layout';
import {
  TableFilterPillView,
  TableFilterPillViewProps,
} from './filterPill/TableFilterPill';
import {
  defaultTableFilterSelectorLabels,
  DropdownOptionViewProps,
  TableFilterSelectorLabels,
  TableFilterSelectorView,
  TableFilterSelectorViewProps,
} from './TableFilterSelector';
import {
  BooleanFilterExpression,
  isValuePresenceOperator,
  ListFilterExpression,
  ListSearch,
  StringFilterExpression,
  TableFilter,
  TableFilterBoolean,
  TableFilterExpression,
  TableFilterList,
  TableFilterListOption,
  TableFilterListOptionCounts,
  TableFilterDate,
  TableFilterNumber,
  TableFilterString,
  DateFilterExpression,
  isRangeOperator,
  NumberFilterExpression,
  RangeSelection,
  VALUE_PRESENCE_OPERATORS,
  ValuePresenceOperator,
} from './types';
import { ButtonGroup, ButtonSize, TertiaryButton } from '@ardoq/button';
import { isTruthy, pluralize } from '@ardoq/common-helpers';
import { NUMBER_FORMAT_PERCENTAGE, Operator } from '@ardoq/api-types';
import { addFilterExpression, upsertFilterExpression } from './utils';
import { createTableFilterExpressionId } from './createTableFilterExpressionId';
import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import {
  defaultTableFilterOperatorLabels,
  getTableFilterBooleanOperatorLabel,
  getTableFilterDateOperatorLabel,
  getTableFilterListOperatorLabel,
  getTableFilterNumberOperatorLabel,
  getTableFilterOperators,
  getTableFilterStringOperatorLabel,
  TableFilterOperatorLabels,
} from './getTableFilterOperators';
import {
  OperatorButtonProps,
  OperatorOption,
} from './filterPill/OperatorButton';
import {
  BooleanPopoverProps,
  DateRangeLabels,
  DateRangePopoverProps,
  ListPopoverProps,
  NumberPopoverProps,
  NumberRangePopoverProps,
  RangeBoundLabels,
  RelativeDatePopoverProps,
  StringPopoverProps,
} from './filterPill/Popover';
import { RelativeDatePickerMode } from '@ardoq/date-time-picker';
import { isDateRangeReversed } from '@ardoq/date-time';
import { ValueButtonProps } from './filterPill/ValueButton';
import { invariant } from '@ardoq/invariant';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';
import { getDateValueLabel } from './getDateValueLabel';
import {
  ARDOQ_DEFAULT_LOCALE,
  percentageEditValueToStoredValue,
  percentageStoredValueToEditValue,
} from '@ardoq/locale';
import {
  defaultTableDisplaySelectorLabels,
  TableDisplaySelectorLabels,
  TableDisplaySelectorView,
  TableDisplaySelectorViewProps,
} from './TableDisplaySelector';
import {
  DashboardPillLabels,
  defaultDashboardPillLabels,
} from './filterPill/dashboardFilterPill/labels';

export type TableFilterPillLabels = {
  removeFilterAriaLabel: string;
  editValueAriaLabel: (filterLabel: string) => string;
  // Label shown in the value button when more than one option is selected on
  // a list filter, e.g. "3 roles". Receives the lower-cased filter label.
  listMultiSelectionLabel: (
    count: number,
    lowerCaseFilterLabel: string
  ) => string;
  dateValueLabel: (value: string) => string;
  // Shown in the value button of a range pill, e.g. "10 – 20".
  rangeLabel: (start: string, end: string) => string;
};

export type TableFilterPopoverLabels = {
  // Used by the StringPopover for the search/filter form label, e.g.
  // "Filter by name" — given the lower-cased filter label.
  stringFilterLabel: (lowerCaseFilterLabel: string) => string;
  stringSubmitLabel: string;
  numberSubmitLabel: string;
  datePlaceholder: string;
  datePickerI18nLocale: I18nLocale;
  numberRange: RangeBoundLabels;
  dateRange: DateRangeLabels;
  loadingLabel: string;
  noResultsLabel: string;
  // Singular/plural item count, defaults to English "1 item" / "N items".
  itemCountLabel: (count: number) => string;
};

export type TableFilterBarLabels = {
  clearButtonLabel: string;
  filterSelector: TableFilterSelectorLabels;
  displaySelector: TableDisplaySelectorLabels;
  pill: TableFilterPillLabels;
  popover: TableFilterPopoverLabels;
  operators: TableFilterOperatorLabels;
  dashboardPill: DashboardPillLabels;
};

export const defaultTableFilterPillLabels: TableFilterPillLabels = {
  removeFilterAriaLabel: 'Remove filter',
  editValueAriaLabel: (filterLabel: string) =>
    `Edit ${filterLabel} filter value`,
  listMultiSelectionLabel: (count, lowerCaseFilterLabel) =>
    `${count} ${pluralize(lowerCaseFilterLabel, count)}`,
  dateValueLabel: getDateValueLabel(
    DEFAULT_ARDOQ_I18NLOCALE,
    ARDOQ_DEFAULT_LOCALE
  ),
  rangeLabel: (start, end) => `${start} – ${end}`,
};

export const defaultTableFilterPopoverLabels: TableFilterPopoverLabels = {
  stringFilterLabel: (lowerCaseFilterLabel: string) =>
    `Filter by ${lowerCaseFilterLabel}`,
  stringSubmitLabel: 'Add filter',
  numberSubmitLabel: 'Apply',
  datePlaceholder: 'Select a date',
  datePickerI18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
  numberRange: {
    fromPrefix: 'From',
    toPrefix: 'To',
    placeholder: 'Search...',
    startHigherError:
      "The 'From' value must be less than the 'To' value. Enter a valid range.",
  },
  dateRange: {
    startLabel: 'Start date',
    endLabel: 'End date',
    startHigherError:
      'Start date should be before end date. Enter a valid range.',
  },
  loadingLabel: 'Loading...',
  noResultsLabel: 'No results',
  itemCountLabel: (count: number) => `${count} ${pluralize('item', count)}`,
};

export const defaultTableFilterBarLabels: TableFilterBarLabels = {
  clearButtonLabel: 'Clear',
  filterSelector: defaultTableFilterSelectorLabels,
  displaySelector: defaultTableDisplaySelectorLabels,
  pill: defaultTableFilterPillLabels,
  popover: defaultTableFilterPopoverLabels,
  operators: defaultTableFilterOperatorLabels,
  dashboardPill: defaultDashboardPillLabels,
};

const mergeLabels = (
  partial: Partial<TableFilterBarLabels> | undefined
): TableFilterBarLabels => ({
  clearButtonLabel:
    partial?.clearButtonLabel ?? defaultTableFilterBarLabels.clearButtonLabel,
  filterSelector: {
    ...defaultTableFilterSelectorLabels,
    ...partial?.filterSelector,
  },
  displaySelector: {
    ...defaultTableDisplaySelectorLabels,
    ...partial?.displaySelector,
  },
  pill: { ...defaultTableFilterPillLabels, ...partial?.pill },
  popover: {
    ...defaultTableFilterPopoverLabels,
    ...partial?.popover,
    numberRange: {
      ...defaultTableFilterPopoverLabels.numberRange,
      ...partial?.popover?.numberRange,
    },
    dateRange: {
      ...defaultTableFilterPopoverLabels.dateRange,
      ...partial?.popover?.dateRange,
    },
  },
  operators: {
    ...defaultTableFilterOperatorLabels,
    ...partial?.operators,
    list: {
      ...defaultTableFilterOperatorLabels.list,
      ...partial?.operators?.list,
    },
    boolean: {
      ...defaultTableFilterOperatorLabels.boolean,
      ...partial?.operators?.boolean,
    },
  },
  dashboardPill: { ...defaultDashboardPillLabels, ...partial?.dashboardPill },
});

export type TableFilterProps<Filter extends TableFilter> = {
  filterExpressions: TableFilterExpression<Filter>[];
  onFilterExpressionChange: (
    filterExpressions: TableFilterExpression<Filter>[]
  ) => void;
  filters: Filter[];
  optionCounts: Record<string, TableFilterListOptionCounts | undefined>;
  displaySelector?: TableDisplaySelectorViewProps | null; // TODO - make non-undefined
  labels?: Partial<TableFilterBarLabels>;
  onListSearchChange?: (search: ListSearch | null) => void;
  /** Ids go into the filter URL param, so they must not contain `,` `;` `|` or `\`. */
  createExpressionId?: () => string;
};

export const TableFilterBar = <Filter extends TableFilter>({
  filters,
  filterExpressions,
  optionCounts,
  onFilterExpressionChange,
  displaySelector,
  labels: partialLabels,
  onListSearchChange,
  createExpressionId = createTableFilterExpressionId,
}: TableFilterProps<Filter>) => {
  const [controlledState, setControlledState] =
    useState<TableFilterControlledState>(createInitialState(filters));

  const openListSearch = toOpenListSearch(controlledState, filters);
  const emittedListSearch = useRef<ListSearch | null>(null);
  useEffect(() => {
    if (isEqual(emittedListSearch.current, openListSearch)) return;
    emittedListSearch.current = openListSearch;
    onListSearchChange?.(openListSearch);
  }, [openListSearch, onListSearchChange]);

  const commands = createCommands(
    filters,
    filterExpressions,
    setControlledState,
    onFilterExpressionChange,
    createExpressionId
  );

  const labels = mergeLabels(partialLabels);

  const tableFilterBarViewProps = toTableFilterBarViewProps(
    filters,
    filterExpressions,
    optionCounts,
    commands,
    controlledState,
    displaySelector ?? null,
    labels
  );

  return <TableFilterBarView {...tableFilterBarViewProps} />;
};

export const toTableFilterBarViewProps = <Filter extends TableFilter>(
  filters: Filter[],
  filterExpressions: TableFilterExpression<Filter>[],
  optionCounts: Record<string, Record<string, number | undefined> | undefined>,
  commands: Commands<Filter>,
  internalState: TableFilterControlledState,
  displaySelector: TableDisplaySelectorViewProps | null,
  labels: TableFilterBarLabels = defaultTableFilterBarLabels
): TableFilterBarViewProps => ({
  filterSelector: toTableFilterSelectorViewProps(
    filters,
    internalState,
    optionCounts,
    commands,
    labels
  ),
  filterPills: filterExpressions
    .map((filterExpression, index) =>
      toTableFilterPillViewProps(
        filterExpression,
        index,
        filters,
        filterExpressions,
        internalState,
        optionCounts,
        commands,
        labels
      )
    )
    .filter(isTruthy),
  clearFilters:
    filterExpressions.length > 0 ? commands.clearFilterExpressions : null,
  displaySelector,
  labels,
});

export type TableFilterBarViewProps = {
  filterSelector: TableFilterSelectorViewProps;
  filterPills: TableFilterPillViewProps[];
  clearFilters: VoidFunction | null;
  displaySelector: TableDisplaySelectorViewProps | null;
  labels?: TableFilterBarLabels;
};

export const TableFilterBarView = (props: TableFilterBarViewProps) => {
  const labels = props.labels ?? defaultTableFilterBarLabels;
  return (
    <FlexBox gap="small" align="start" justify="space-between">
      <Inline gap="small">
        <TableFilterSelectorView {...props.filterSelector} />
        {props.filterPills.map((pillProps, index) => (
          <TableFilterPillView {...pillProps} key={index} />
        ))}
      </Inline>
      <ButtonGroup>
        {props.clearFilters && (
          <TertiaryButton
            buttonSize={ButtonSize.SMALL}
            onClick={props.clearFilters}
          >
            {labels.clearButtonLabel}
          </TertiaryButton>
        )}
        {props.displaySelector && (
          <TableDisplaySelectorView {...props.displaySelector} />
        )}
      </ButtonGroup>
    </FlexBox>
  );
};

export const createInitialState = (
  filters: TableFilter[] = []
): TableFilterControlledState => ({
  openDropdown: null,
  sessionFilterExpressions: new Map(),
  stringFilterValues: Object.fromEntries(
    filters.filter(f => f.type === 'string').map(filter => [filter.id, ''])
  ),
  listFilterSearchStrings: Object.fromEntries(
    filters.filter(f => f.type === 'list').map(filter => [filter.id, ''])
  ),
  numberFilterValues: Object.fromEntries(
    filters.filter(f => f.type === 'number').map(filter => [filter.id, null])
  ),
  pillStringValues: {},
  pillNumberValues: {},
});

const toOpenFilterId = ({
  openDropdown,
}: TableFilterControlledState): string | null => {
  if (openDropdown?.type === 'selector') return openDropdown.nestedFilterId;
  if (openDropdown?.segment === 'value') return openDropdown.filterId;
  return null;
};

const toOpenListSearch = (
  internalState: TableFilterControlledState,
  filters: TableFilter[]
): ListSearch | null => {
  const openFilterId = toOpenFilterId(internalState);
  const openFilter = filters.find(filter => filter.id === openFilterId);
  if (openFilter?.type !== 'list' || !openFilter.remoteSearch) return null;

  return {
    filterId: openFilter.id,
    query: internalState.listFilterSearchStrings[openFilter.id] ?? '',
  };
};

const createCommands = <Filter extends TableFilter>(
  filters: Filter[],
  filterExpressions: TableFilterExpression<Filter>[],
  setControlledState: Dispatch<SetStateAction<TableFilterControlledState>>,
  onFilterExpressionChange: (
    filterExpressions: TableFilterExpression<Filter>[]
  ) => void,
  createExpressionId: () => string
): Commands<Filter> => ({
  setMainPopoverOpen: (isOpen: boolean) => {
    const initialState = createInitialState(filters);
    setControlledState(currentState => {
      if (isOpen) return openSelectorDropdown(initialState);
      if (!isSelectorDropdownOpen(currentState)) return currentState;
      return initialState;
    });
  },
  updateFilterExpression: (filterExpression: TableFilterExpression<Filter>) => {
    onFilterExpressionChange(
      upsertFilterExpression(filterExpression, filterExpressions)
    );

    setControlledState(currentState =>
      updateSessionFilterExpression(currentState, filterExpression)
    );
  },
  createFilterExpressions: (
    filterId: string,
    newFilterExpression: TableFilterExpression<Filter>
  ) => {
    const filterExpression = {
      ...newFilterExpression,
      id: createExpressionId(),
    };
    onFilterExpressionChange(
      addFilterExpression(filterExpression, filterExpressions)
    );
    setControlledState(currentState =>
      addSessionFilterExpression(currentState, { filterId, filterExpression })
    );
  },
  clearFilterExpressions: () => {
    onFilterExpressionChange([]);
    setControlledState(closeOpenDropdown);
  },
  setNestedPopoverOpen: (filterId: string, isOpen: boolean) => {
    setControlledState(currentState =>
      setNestedFilterDropdownOpen(currentState, { filterId, isOpen })
    );
  },
  setStringFilterValue: (filterId: string, value: string) => {
    setControlledState(currentState => ({
      ...currentState,
      stringFilterValues: {
        ...currentState.stringFilterValues,
        [filterId]: value,
      },
    }));
  },
  setListFilterSearchString: (filterId: string, value: string) => {
    setControlledState(currentState => ({
      ...currentState,
      listFilterSearchStrings: {
        ...currentState.listFilterSearchStrings,
        [filterId]: value,
      },
    }));
  },
  setOperatorPopoverOpen: (filterExpressionKey: string, isOpen: boolean) => {
    setControlledState(currentState =>
      setPillDropdownOpen(currentState, {
        isOpen,
        target: { key: filterExpressionKey, segment: 'operator' },
      })
    );
  },
  setValuePopoverOpen: (
    { key, filterId }: ValuePopoverTarget,
    isOpen: boolean
  ) => {
    setControlledState(currentState =>
      setPillDropdownOpen(currentState, {
        isOpen,
        target: { key, segment: 'value', filterId },
      })
    );
  },
  setPillStringValue: (filterExpressionKey: string, value: string) => {
    setControlledState(currentState => ({
      ...currentState,
      pillStringValues: {
        ...currentState.pillStringValues,
        [filterExpressionKey]: value,
      },
    }));
  },
  setNumberFilterValue: (filterId: string, value: number | null) => {
    setControlledState(currentState => ({
      ...currentState,
      numberFilterValues: {
        ...currentState.numberFilterValues,
        [filterId]: value,
      },
    }));
  },
  setPillNumberValue: (filterExpressionKey: string, value: number | null) => {
    setControlledState(currentState => ({
      ...currentState,
      pillNumberValues: {
        ...currentState.pillNumberValues,
        [filterExpressionKey]: value,
      },
    }));
  },
  onFilterExpressionChange,
});

const isSelectorDropdownOpen = (state: TableFilterControlledState): boolean =>
  state.openDropdown?.type === 'selector';

const openSelectorDropdown = (
  initialState: TableFilterControlledState
): TableFilterControlledState => ({
  ...initialState,
  openDropdown: { type: 'selector', nestedFilterId: null },
});

const updateSessionFilterExpression = (
  currentState: TableFilterControlledState,
  filterExpression: TableFilterExpression
): TableFilterControlledState => {
  const sessionFilterExpressions = new Map(
    currentState.sessionFilterExpressions
  );
  if (
    filterExpression.type === 'boolean' ||
    filterExpression.selection.length !== 0
  ) {
    sessionFilterExpressions.set(filterExpression.filterId, filterExpression);
  } else {
    sessionFilterExpressions.delete(filterExpression.filterId);
  }
  return { ...currentState, sessionFilterExpressions };
};

type AddSessionFilterExpressionArgs = {
  filterId: string;
  filterExpression: TableFilterExpression;
};

const addSessionFilterExpression = (
  currentState: TableFilterControlledState,
  { filterId, filterExpression }: AddSessionFilterExpressionArgs
): TableFilterControlledState => ({
  ...currentState,
  sessionFilterExpressions: new Map(currentState.sessionFilterExpressions).set(
    filterId,
    filterExpression
  ),
});

const setNestedFilterDropdownOpen = (
  currentState: TableFilterControlledState,
  { filterId, isOpen }: { filterId: string; isOpen: boolean }
): TableFilterControlledState => {
  if (isOpen) {
    return {
      ...currentState,
      openDropdown: { type: 'selector', nestedFilterId: filterId },
    };
  }
  if (toNestedFilterId(currentState.openDropdown) !== filterId) {
    return currentState;
  }
  return {
    ...currentState,
    openDropdown: { type: 'selector', nestedFilterId: null },
  };
};

const toNestedFilterId = (openDropdown: OpenDropdown | null): string | null =>
  openDropdown?.type === 'selector' ? openDropdown.nestedFilterId : null;

type SetPillDropdownOpenArgs = { isOpen: boolean; target: PillDropdownTarget };

const setPillDropdownOpen = (
  currentState: TableFilterControlledState,
  { isOpen, target }: SetPillDropdownOpenArgs
): TableFilterControlledState => {
  if (isOpen) {
    return {
      ...closeOpenDropdown(currentState),
      openDropdown: { type: 'pill', ...target },
    };
  }
  if (!isPillDropdownOpen(currentState.openDropdown, target)) {
    return currentState;
  }
  return closeOpenDropdown(currentState);
};

const closeOpenDropdown = (
  currentState: TableFilterControlledState
): TableFilterControlledState => {
  const openFilterId = toOpenFilterId(currentState);
  if (openFilterId === null) return { ...currentState, openDropdown: null };
  return {
    ...currentState,
    openDropdown: null,
    listFilterSearchStrings: {
      ...currentState.listFilterSearchStrings,
      [openFilterId]: '',
    },
  };
};

type ValuePopoverTarget = { key: string; filterId: string };

type PillDropdownTarget =
  | { key: string; segment: 'operator' }
  | (ValuePopoverTarget & { segment: 'value' });

export type OpenDropdown =
  | { type: 'selector'; nestedFilterId: string | null }
  | ({ type: 'pill' } & PillDropdownTarget);

export type TableFilterControlledState = {
  openDropdown: OpenDropdown | null;
  sessionFilterExpressions: Map<string, TableFilterExpression>;
  stringFilterValues: Record<string, string>;
  listFilterSearchStrings: Record<string, string>;
  numberFilterValues: Record<string, number | null>;
  pillStringValues: Record<string, string>;
  pillNumberValues: Record<string, number | null>;
};

const isPillDropdownOpen = (
  openDropdown: OpenDropdown | null,
  { key, segment }: Pick<PillDropdownTarget, 'key' | 'segment'>
): boolean =>
  openDropdown?.type === 'pill' &&
  openDropdown.key === key &&
  openDropdown.segment === segment;

export type Commands<
  Filter extends TableFilter,
  Expression extends TableFilterExpression<Filter> =
    TableFilterExpression<Filter>,
> = {
  updateFilterExpression: (filterExpression: Expression) => void;
  createFilterExpressions: (
    filterId: string,
    filterExpression: Expression
  ) => void;
  clearFilterExpressions: VoidFunction;
  setNestedPopoverOpen: (filterId: string, isOpen: boolean) => void;
  setStringFilterValue: (filterId: string, value: string) => void;
  setListFilterSearchString: (filterId: string, value: string) => void;
  setMainPopoverOpen: (isOpen: boolean) => void;
  setOperatorPopoverOpen: (
    filterExpressionKey: string,
    isOpen: boolean
  ) => void;
  setValuePopoverOpen: (target: ValuePopoverTarget, isOpen: boolean) => void;
  setPillStringValue: (filterExpressionKey: string, value: string) => void;
  setNumberFilterValue: (filterId: string, value: number | null) => void;
  setPillNumberValue: (
    filterExpressionKey: string,
    value: number | null
  ) => void;
  onFilterExpressionChange: (filterExpressions: Expression[]) => void;
};

export const toTableFilterPillViewProps = <Filter extends TableFilter>(
  filterExpression: TableFilterExpression<Filter>,
  index: number,
  filters: Filter[],
  filterExpressions: TableFilterExpression<Filter>[],
  internalState: TableFilterControlledState,
  optionCounts: Record<string, Record<string, number | undefined> | undefined>,
  commands: Commands<Filter>,
  labels: TableFilterBarLabels = defaultTableFilterBarLabels
): TableFilterPillViewProps | null => {
  const filter = filters.find(
    filter => filter.id === filterExpression.filterId
  );
  if (!filter) {
    return null;
  }

  const filterExpressionKey = `${filterExpression.filterId}-${index}`;
  const updateFilter = (newFilterExpression: TableFilterExpression<Filter>) =>
    commands.onFilterExpressionChange(
      filterExpressions.toSpliced(index, 1, newFilterExpression)
    );

  const isOperatorPopoverOpen = isPillDropdownOpen(internalState.openDropdown, {
    key: filterExpressionKey,
    segment: 'operator',
  });

  const operatorButtonProps = toOperatorButtonProps(
    filterExpression,
    filter,
    updateFilter,
    isOperatorPopoverOpen,
    filterExpressionKey,
    commands,
    labels.operators
  );

  if (filterExpression.type === 'boolean') {
    invariant(
      filter.type === 'boolean',
      `Unsupported filter type: ${filter.type}`
    );
    return {
      label: { label: filter.label, icon: filter.icon },
      operator: operatorButtonProps,
      value: filter.value,
      onRemove: () =>
        commands.onFilterExpressionChange(
          filterExpressions.toSpliced(index, 1)
        ),
      removeFilterAriaLabel: labels.pill.removeFilterAriaLabel,
    };
  }

  if (isValuePresenceOperator(filterExpression.operator)) {
    return {
      label: { label: filter.label, icon: filter.icon },
      operator: operatorButtonProps,
      value: null,
      onRemove: () =>
        commands.onFilterExpressionChange(
          filterExpressions.toSpliced(index, 1)
        ),
      removeFilterAriaLabel: labels.pill.removeFilterAriaLabel,
    };
  }

  const pillStringValue =
    internalState.pillStringValues[filterExpressionKey] ??
    (filterExpression.type === 'string' ? filterExpression.selection : '');

  const pillNumberValue =
    internalState.pillNumberValues[filterExpressionKey] ??
    (filterExpression.type === 'number' && filter.type === 'number'
      ? toEditNumber(String(filterExpression.selection), filter)
      : null);

  const isValuePopoverOpen = isPillDropdownOpen(internalState.openDropdown, {
    key: filterExpressionKey,
    segment: 'value',
  });

  // Boolean is handled above with an early return
  type ValuedFilter =
    | TableFilterList
    | TableFilterString
    | TableFilterNumber
    | TableFilterDate;
  const valueButtonProps = toValueButtonProps(
    filterExpression as TableFilterExpression<ValuedFilter>,
    updateFilter as (filter: TableFilterExpression<ValuedFilter>) => void,
    pillStringValue,
    pillNumberValue,
    internalState.listFilterSearchStrings[filterExpression.filterId] ?? '',
    filter as ValuedFilter,
    isValuePopoverOpen,
    optionCounts[filterExpression.filterId] || {},
    filterExpressionKey,
    commands as Commands<ValuedFilter>,
    labels
  );

  return {
    label: { label: filter.label, icon: filter.icon },
    operator: operatorButtonProps,
    value: valueButtonProps,
    onRemove: () =>
      commands.onFilterExpressionChange(filterExpressions.toSpliced(index, 1)),
    removeFilterAriaLabel: labels.pill.removeFilterAriaLabel,
  };
};

const toSelectionForOperator = (
  selection: string | RangeSelection,
  operator: Operator
): string | RangeSelection => {
  if (isValuePresenceOperator(operator)) return '';

  const [start] = Array.isArray(selection) ? selection : [selection];
  return isRangeOperator(operator) ? [start, ''] : start;
};

const operatorsForFilter = <Operators extends Operator>(
  typeOperators: readonly Operators[],
  declared: readonly Operator[] | undefined
): (Operators | ValuePresenceOperator)[] => {
  if (!declared) return [...typeOperators];

  const offerable: (Operators | ValuePresenceOperator)[] = [
    ...typeOperators,
    ...VALUE_PRESENCE_OPERATORS,
  ];
  return offerable.filter(operator => declared.includes(operator));
};

const toInitialOperator = <Operators extends Operator>(
  typeOperators: readonly Operators[],
  filter: TableFilter,
  preferred: Operators
): Operators | ValuePresenceOperator => {
  const valueOperators = operatorsForFilter(
    typeOperators,
    filter.operators
  ).filter(operator => !isValuePresenceOperator(operator));
  return (
    valueOperators.find(operator => operator === preferred) ??
    valueOperators[0] ??
    preferred
  );
};

const getOperatorOptions = <Filter extends TableFilterExpression>(
  filterExpression: Filter,
  filter: TableFilter,
  updateFilter: (newExpression: Filter) => void,
  closePopover: VoidFunction,
  operatorLabels: TableFilterOperatorLabels = defaultTableFilterOperatorLabels
): OperatorOption[] => {
  const declaredOperators = filter.operators && [
    ...filter.operators,
    filterExpression.operator,
  ];

  switch (filterExpression.type) {
    case 'string':
      return operatorsForFilter(
        getTableFilterOperators(filterExpression.type),
        declaredOperators
      ).map(operator => ({
        value: operator,
        label: getTableFilterStringOperatorLabel(operator, operatorLabels),
        onClick: () => {
          closePopover();
          updateFilter({
            ...filterExpression,
            operator,
            selection: isValuePresenceOperator(operator)
              ? ''
              : filterExpression.selection,
          });
        },
      }));

    case 'list':
      return operatorsForFilter(
        getTableFilterOperators(filterExpression.type),
        declaredOperators
      ).map(operator => ({
        value: operator,
        label: getTableFilterListOperatorLabel(
          operator,
          filterExpression.selection.length > 1,
          operatorLabels
        ),
        onClick: () => {
          closePopover();
          updateFilter({
            ...filterExpression,
            operator,
            selection: isValuePresenceOperator(operator)
              ? []
              : filterExpression.selection,
          });
        },
      }));

    case 'number':
      return operatorsForFilter(
        getTableFilterOperators(filterExpression.type),
        declaredOperators
      ).map(operator => ({
        value: operator,
        label: getTableFilterNumberOperatorLabel(operator, operatorLabels),
        onClick: () => {
          closePopover();
          updateFilter({
            ...filterExpression,
            operator,
            selection: toSelectionForOperator(
              filterExpression.selection,
              operator
            ),
          });
        },
      }));

    case 'date':
      return operatorsForFilter(
        getTableFilterOperators(filterExpression.type),
        declaredOperators
      ).map(operator => ({
        value: operator,
        label: getTableFilterDateOperatorLabel(operator, operatorLabels),
        onClick: () => {
          closePopover();
          updateFilter({
            ...filterExpression,
            operator,
            selection: toSelectionForOperator(
              filterExpression.selection,
              operator
            ),
          });
        },
      }));

    case 'boolean':
      return getTableFilterOperators(filterExpression.type).map(operator => ({
        value: operator,
        label: getTableFilterBooleanOperatorLabel(operator, operatorLabels),
        onClick: () => {
          closePopover();
          updateFilter({ ...filterExpression, operator });
        },
      }));
  }
};

const toValueButtonLabel = (
  filter:
    | TableFilterList
    | TableFilterString
    | TableFilterNumber
    | TableFilterDate,
  filterExpression:
    | ListFilterExpression
    | StringFilterExpression
    | NumberFilterExpression
    | DateFilterExpression,
  pillLabels: TableFilterPillLabels = defaultTableFilterPillLabels
): string => {
  if (filterExpression.type === 'date') {
    const { operator, selection } = filterExpression;
    if (isRangeOperator(operator) && Array.isArray(selection)) {
      return pillLabels.rangeLabel(
        pillLabels.dateValueLabel(selection[0]),
        pillLabels.dateValueLabel(selection[1])
      );
    }
    return pillLabels.dateValueLabel(String(selection));
  }

  if (filterExpression.type === 'string') {
    return filterExpression.selection;
  }

  if (filterExpression.type === 'number') {
    invariant(
      filter.type === 'number',
      `Unsupported filter type: ${filter.type}`
    );
    const { operator, selection } = filterExpression;
    if (isRangeOperator(operator) && Array.isArray(selection)) {
      return pillLabels.rangeLabel(
        toNumberLabel(selection[0], filter),
        toNumberLabel(selection[1], filter)
      );
    }
    return toNumberLabel(String(selection), filter);
  }

  if (filterExpression.selection.length > 1) {
    return pillLabels.listMultiSelectionLabel(
      filterExpression.selection.length,
      filter.label.toLowerCase()
    );
  }

  // This is needed to narrow the type as filter and filterExpression are not
  // automatically correlated
  invariant(filter.type === 'list', `Unsupported filter type: ${filter.type}`);

  const expressionId = filterExpression.selection[0] || '';
  return filter.options.find(option => option.id === expressionId)?.label || '';
};

const toOperatorButtonProps = <Filter extends TableFilter>(
  filterExpression: TableFilterExpression<Filter>,
  filter: TableFilter,
  updateFilter: (filter: TableFilterExpression<Filter>) => void,
  isPopoverOpen: boolean,
  filterExpressionKey: string,
  commands: Commands<Filter>,
  operatorLabels: TableFilterOperatorLabels = defaultTableFilterOperatorLabels
): OperatorButtonProps => {
  const options = getOperatorOptions(
    filterExpression,
    filter,
    updateFilter,
    () => commands.setOperatorPopoverOpen(filterExpressionKey, false),
    operatorLabels
  );
  const currentOperator = options.find(
    option => option.value === filterExpression.operator
  );

  return {
    type: 'button',
    button: {
      isPopoverOpen: isPopoverOpen,
      label: currentOperator?.label ?? '',
      onClick: () =>
        commands.setOperatorPopoverOpen(filterExpressionKey, !isPopoverOpen),
    },
    popover: {
      isOpen: isPopoverOpen,
      onClose: () =>
        commands.setOperatorPopoverOpen(filterExpressionKey, false),
      options,
    },
  };
};

const toStringPopoverProps = (
  filterExpression: TableFilterExpression<TableFilterString>,
  updateFilter: (filter: TableFilterExpression<TableFilterString>) => void,
  stringValue: string,
  filter: TableFilter,
  filterExpressionKey: string,
  commands: Commands<TableFilterString>,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
): StringPopoverProps => ({
  type: 'string' as const,
  value: stringValue,
  isDisabled: stringValue === '',
  label: popoverLabels.stringFilterLabel(filter.label.toLowerCase()),
  submitLabel: popoverLabels.stringSubmitLabel,
  setValue: value => commands.setPillStringValue(filterExpressionKey, value),
  onFormSubmit: () => {
    if (stringValue === '') {
      return;
    }
    updateFilter({
      ...filterExpression,
      selection: stringValue,
    });
    commands.setValuePopoverOpen(
      { key: filterExpressionKey, filterId: filter.id },
      false
    );
  },
});

/** `NumberInput` reports a cleared field as `NaN`, not as null. */
const toEnteredNumber = (value: number | null): number | null =>
  value === null || !isFinite(value) ? null : value;

const toDateMode = (filter: TableFilterDate): RelativeDatePickerMode =>
  filter.withTime
    ? RelativeDatePickerMode.DATE_TIME
    : RelativeDatePickerMode.DATE;

const toDatePopoverProps = (
  filterExpression: TableFilterExpression<TableFilterDate>,
  updateFilter: (filter: TableFilterExpression<TableFilterDate>) => void,
  filter: TableFilterDate,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
): RelativeDatePopoverProps => ({
  type: 'relativeDate' as const,
  value: [filterExpression.selection].flat()[0] || undefined,
  label: popoverLabels.stringFilterLabel(filter.label.toLowerCase()),
  placeholder: popoverLabels.datePlaceholder,
  i18nLocale: popoverLabels.datePickerI18nLocale,
  mode: toDateMode(filter),
  // The picker has no submit button, so a pick is the whole interaction. It
  // stays open, as the sidebar's does, so a mistyped date can be corrected.
  // Clearing it leaves the filter as it was rather than applying a blank date.
  setValue: value => {
    if (!value) {
      return;
    }
    updateFilter({ ...filterExpression, selection: value });
  },
});

const toDateRangePopoverProps = (
  filterExpression: TableFilterExpression<TableFilterDate>,
  updateFilter: (filter: TableFilterExpression<TableFilterDate>) => void,
  filter: TableFilterDate,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
): DateRangePopoverProps => {
  const [start, end] = Array.isArray(filterExpression.selection)
    ? filterExpression.selection
    : ['', ''];

  return {
    type: 'dateRange' as const,
    start: start || undefined,
    end: end || undefined,
    isStartAfterEnd: isDateRangeReversed({ start, end }),
    mode: toDateMode(filter),
    placeholder: popoverLabels.datePlaceholder,
    i18nLocale: popoverLabels.datePickerI18nLocale,
    labels: popoverLabels.dateRange,
    setBounds: bounds =>
      updateFilter({
        ...filterExpression,
        selection: [bounds.start ?? '', bounds.end ?? ''],
      }),
  };
};

const toNumberRangePopoverProps = (
  filterExpression: TableFilterExpression<TableFilterNumber>,
  updateFilter: (filter: TableFilterExpression<TableFilterNumber>) => void,
  filter: TableFilterNumber,
  filterExpressionKey: string,
  commands: Commands<TableFilterNumber>,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
): NumberRangePopoverProps => {
  const [start = '', end = ''] = Array.isArray(filterExpression.selection)
    ? filterExpression.selection
    : [];

  return {
    type: 'numberRange' as const,
    start: toEditNumber(start, filter),
    end: toEditNumber(end, filter),
    labels: popoverLabels.numberRange,
    submitLabel: popoverLabels.numberSubmitLabel,
    onSubmit: bounds => {
      updateFilter({
        ...filterExpression,
        selection: [
          fromEditNumber(bounds.start, filter),
          fromEditNumber(bounds.end, filter),
        ],
      });
      commands.setValuePopoverOpen(
        { key: filterExpressionKey, filterId: filter.id },
        false
      );
    },
  };
};

const toNumberLabel = (
  storedValue: string,
  filter: TableFilterNumber
): string => {
  const editValue = toEditNumber(storedValue, filter);
  return editValue === null ? storedValue : String(editValue);
};

const toEditNumber = (
  storedValue: string,
  filter: TableFilterNumber
): number | null => {
  if (storedValue === '') return null;
  const parsed = Number(storedValue);
  if (isNaN(parsed)) return null;
  return filter.numberFormat === NUMBER_FORMAT_PERCENTAGE
    ? percentageStoredValueToEditValue(parsed)
    : parsed;
};

const fromEditNumber = (editValue: number, filter: TableFilterNumber): string =>
  String(
    filter.numberFormat === NUMBER_FORMAT_PERCENTAGE
      ? percentageEditValueToStoredValue(editValue)
      : editValue
  );

const toNumberPopoverProps = (
  filterExpression: TableFilterExpression<TableFilterNumber>,
  updateFilter: (filter: TableFilterExpression<TableFilterNumber>) => void,
  numberValue: number | null,
  filter: TableFilterNumber,
  filterExpressionKey: string,
  commands: Commands<TableFilterNumber>,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
): NumberPopoverProps => ({
  type: 'number' as const,
  value: numberValue,
  isDisabled: toEnteredNumber(numberValue) === null,
  allowDecimals: true,
  label: popoverLabels.stringFilterLabel(filter.label.toLowerCase()),
  submitLabel: popoverLabels.numberSubmitLabel,
  setValue: value => commands.setPillNumberValue(filterExpressionKey, value),
  onFormSubmit: () => {
    const entered = toEnteredNumber(numberValue);
    if (entered === null) {
      return;
    }
    updateFilter({
      ...filterExpression,
      selection: fromEditNumber(entered, filter),
    });
    commands.setValuePopoverOpen(
      { key: filterExpressionKey, filterId: filter.id },
      false
    );
  },
});

const toListSearchProps = (
  filter: TableFilterList,
  searchString: string,
  commands: Commands<TableFilterList>,
  popoverLabels: TableFilterPopoverLabels
): NonNullable<ListPopoverProps['filter']> => ({
  isLoading: filter.remoteSearch?.isLoading ?? false,
  ariaLabel: popoverLabels.stringFilterLabel(filter.label.toLowerCase()),
  value: searchString,
  onChange: (value: string) =>
    commands.setListFilterSearchString(filter.id, value),
});

const toListPopoverProps = (
  filterExpression: TableFilterExpression<TableFilterList>,
  updateFilter: (filter: TableFilterExpression<TableFilterList>) => void,
  filter: TableFilterList,
  optionCounts: Record<string, number | undefined>,
  filterExpressionKey: string,
  commands: Commands<TableFilterList>,
  searchString: string,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
): ListPopoverProps => ({
  type: 'list' as const,
  loadingLabel: popoverLabels.loadingLabel,
  noResultsLabel: popoverLabels.noResultsLabel,
  filter: filter.remoteSearch
    ? toListSearchProps(filter, searchString, commands, popoverLabels)
    : undefined,
  groups: [
    {
      options: filter.options.map(option => {
        const isChecked =
          filterExpression.type === 'list'
            ? filterExpression.selection.some(
                selected => selected === option.id
              )
            : false;
        return {
          label: option.label,
          isChecked,
          id: option.id,
          icon: option.icon || null,
          rightIcon: null,
          count: optionCounts?.[option.id] || null,
          itemCountLabel: popoverLabels.itemCountLabel,
          onClick: () => {
            if (
              !filterExpression ||
              !option ||
              typeof filterExpression.selection === 'string'
            ) {
              return;
            }

            const isSelected = filterExpression.selection.some(
              selected => selected === option.id
            );
            const newSelection = isSelected
              ? filterExpression.selection.filter(
                  selected => selected !== option.id
                )
              : [...filterExpression.selection, option.id];

            updateFilter({
              ...filterExpression,
              selection: newSelection,
            });
            commands.setValuePopoverOpen(
              { key: filterExpressionKey, filterId: filter.id },
              false
            );
          },
          onCheckboxClick: () => {
            if (
              !filterExpression ||
              !option ||
              typeof filterExpression.selection === 'string'
            ) {
              return;
            }

            const isSelected = filterExpression.selection.some(
              selected => selected === option.id
            );
            const newSelection = isSelected
              ? filterExpression.selection.filter(
                  selected => selected !== option.id
                )
              : [...filterExpression.selection, option.id];

            updateFilter({
              ...filterExpression,
              selection: newSelection,
            });
          },
        };
      }),
    },
  ],
});

const toValueButtonPopoverContent = <
  Filter extends
    | TableFilterList
    | TableFilterString
    | TableFilterNumber
    | TableFilterDate,
>(
  filter: Filter,
  filterExpression: TableFilterExpression<Filter>,
  updateFilter: (filter: TableFilterExpression<Filter>) => void,
  stringValue: string,
  numberValue: number | null,
  listSearchString: string,
  optionCounts: Record<string, number | undefined>,
  filterExpressionKey: string,
  commands: Commands<Filter>,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
):
  | ListPopoverProps
  | StringPopoverProps
  | NumberPopoverProps
  | NumberRangePopoverProps
  | DateRangePopoverProps
  | RelativeDatePopoverProps => {
  switch (filter.type) {
    case 'date':
      if (isRangeOperator(filterExpression.operator)) {
        return toDateRangePopoverProps(
          filterExpression as TableFilterExpression<TableFilterDate>,
          updateFilter as (
            filter: TableFilterExpression<TableFilterDate>
          ) => void,
          filter,
          popoverLabels
        );
      }
      return toDatePopoverProps(
        filterExpression as TableFilterExpression<TableFilterDate>,
        updateFilter as (
          filter: TableFilterExpression<TableFilterDate>
        ) => void,
        filter,
        popoverLabels
      );
    case 'number':
      if (isRangeOperator(filterExpression.operator)) {
        return toNumberRangePopoverProps(
          filterExpression as TableFilterExpression<TableFilterNumber>,
          updateFilter as (
            filter: TableFilterExpression<TableFilterNumber>
          ) => void,
          filter,
          filterExpressionKey,
          commands as Commands<TableFilterNumber>,
          popoverLabels
        );
      }
      return toNumberPopoverProps(
        filterExpression as TableFilterExpression<TableFilterNumber>,
        updateFilter as (
          filter: TableFilterExpression<TableFilterNumber>
        ) => void,
        numberValue,
        filter,
        filterExpressionKey,
        commands as Commands<TableFilterNumber>,
        popoverLabels
      );
    case 'string':
      return toStringPopoverProps(
        filterExpression as TableFilterExpression<TableFilterString>,
        updateFilter as (
          filter: TableFilterExpression<TableFilterString>
        ) => void,
        stringValue,
        filter,
        filterExpressionKey,
        commands as Commands<TableFilterString>,
        popoverLabels
      );
    case 'list':
      return toListPopoverProps(
        filterExpression as TableFilterExpression<TableFilterList>,
        updateFilter as (
          filter: TableFilterExpression<TableFilterList>
        ) => void,
        filter as TableFilterList,
        optionCounts,
        filterExpressionKey,
        commands as Commands<TableFilterList>,
        listSearchString,
        popoverLabels
      );
  }
};

const toValueButtonProps = <
  Filter extends
    | TableFilterList
    | TableFilterString
    | TableFilterNumber
    | TableFilterDate,
>(
  filterExpression: TableFilterExpression<Filter>,
  updateFilter: (filter: TableFilterExpression<Filter>) => void,
  stringValue: string,
  numberValue: number | null,
  listSearchString: string,
  filter: Filter,
  isPopoverOpen: boolean,
  optionCounts: Record<string, number | undefined>,
  filterExpressionKey: string,
  commands: Commands<Filter>,
  labels: TableFilterBarLabels = defaultTableFilterBarLabels
): ValueButtonProps => ({
  button: {
    isPopoverOpen,
    onClick: () =>
      commands.setValuePopoverOpen(
        { key: filterExpressionKey, filterId: filter.id },
        !isPopoverOpen
      ),
    label: toValueButtonLabel(filter, filterExpression, labels.pill),
  },
  popover: {
    content: toValueButtonPopoverContent(
      filter,
      filterExpression,
      updateFilter,
      stringValue,
      numberValue,
      listSearchString,
      optionCounts,
      filterExpressionKey,
      commands,
      labels.popover
    ),
    isOpen: isPopoverOpen,
    onClose: () =>
      commands.setValuePopoverOpen(
        { key: filterExpressionKey, filterId: filter.id },
        false
      ),
  },
  filterLabel: filter.label,
  editValueAriaLabel: labels.pill.editValueAriaLabel,
});

const toTableFilterSelectorViewProps = <Filter extends TableFilter>(
  filters: Filter[],
  internalState: TableFilterControlledState,
  optionCounts: Record<string, Record<string, number | undefined> | undefined>,
  commands: Commands<Filter>,
  labels: TableFilterBarLabels = defaultTableFilterBarLabels
): TableFilterSelectorViewProps => ({
  popover: {
    isOpen: internalState.openDropdown?.type === 'selector',
    options: filters.map(filter =>
      toDropdownOptionViewProps(
        filter,
        internalState,
        optionCounts,
        commands,
        labels
      )
    ),
    setPopoverOpen: commands.setMainPopoverOpen,
  },
  labels: labels.filterSelector,
});

const toDropdownOptionViewProps = <Filter extends TableFilter>(
  filter: Filter,
  internalState: TableFilterControlledState,
  optionCounts: Record<string, Record<string, number | undefined> | undefined>,
  commands: Commands<Filter>,
  labels: TableFilterBarLabels = defaultTableFilterBarLabels
): DropdownOptionViewProps => ({
  popover: {
    isOpen: toNestedFilterId(internalState.openDropdown) === filter.id,
    setIsOpen: isOpen => {
      commands.setNestedPopoverOpen(filter.id, isOpen);
    },
    content: toContent(filter, internalState, optionCounts, commands, labels),
  },
  icon: filter.icon || null,
  label: filter.type === 'boolean' ? filter.value : filter.label,
});

const toContent = <Filter extends TableFilter>(
  filter: Filter,
  internalState: TableFilterControlledState,
  optionCounts: Record<string, Record<string, number | undefined> | undefined>,
  commands: Commands<Filter>,
  labels: TableFilterBarLabels = defaultTableFilterBarLabels
):
  | BooleanPopoverProps
  | StringPopoverProps
  | NumberPopoverProps
  | RelativeDatePopoverProps
  | ListPopoverProps => {
  switch (filter.type) {
    case 'date':
      return toDateContent(
        filter,
        commands as Commands<TableFilterDate>,
        labels.popover
      );
    case 'number':
      return toNumberContent(
        filter,
        internalState,
        commands as Commands<TableFilterNumber>,
        labels.popover
      );
    case 'string':
      return toStringContent(
        filter,
        internalState,
        commands as Commands<TableFilterString>,
        labels.popover
      );
    case 'list':
      return toListContent(
        filter,
        optionCounts[filter.id] || {},
        internalState,
        commands as Commands<TableFilterList>,
        labels.popover
      );
    case 'boolean':
      return toBooleanContent(filter, commands as Commands<TableFilterBoolean>);
  }
};

const toStringContent = (
  filter: TableFilterString,
  internalState: TableFilterControlledState,
  commands: Commands<TableFilterString>,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
): StringPopoverProps => ({
  type: 'string' as const,
  value: internalState.stringFilterValues[filter.id] || '',
  isDisabled: Boolean(internalState.stringFilterValues[filter.id] === ''),
  label: popoverLabels.stringFilterLabel(filter.label.toLowerCase()),
  submitLabel: popoverLabels.stringSubmitLabel,
  setValue: (stringValue: string) => {
    commands.setStringFilterValue(filter.id, stringValue);
  },
  onFormSubmit: () => {
    const filterExpression: StringFilterExpression = {
      type: 'string',
      filterId: filter.id,
      operator: toInitialOperator(
        getTableFilterOperators('string'),
        filter,
        Operator.CONTAINS
      ),
      selection: internalState.stringFilterValues[filter.id] || '',
    };

    commands.createFilterExpressions(filter.id, filterExpression);
    commands.setMainPopoverOpen(false);
  },
});

const toDateContent = (
  filter: TableFilterDate,
  commands: Commands<TableFilterDate>,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
): RelativeDatePopoverProps => ({
  type: 'relativeDate' as const,
  value: undefined,
  label: popoverLabels.stringFilterLabel(filter.label.toLowerCase()),
  placeholder: popoverLabels.datePlaceholder,
  i18nLocale: popoverLabels.datePickerI18nLocale,
  mode: toDateMode(filter),
  setValue: value => {
    if (!value) {
      return;
    }
    const filterExpression: DateFilterExpression = {
      type: 'date',
      filterId: filter.id,
      operator: toInitialOperator(
        getTableFilterOperators('date'),
        filter,
        Operator.EQUAL
      ),
      selection: value,
    };

    commands.createFilterExpressions(filter.id, filterExpression);
    commands.setMainPopoverOpen(false);
  },
});

const toNumberContent = (
  filter: TableFilterNumber,
  internalState: TableFilterControlledState,
  commands: Commands<TableFilterNumber>,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
): NumberPopoverProps => {
  const value = toEnteredNumber(
    internalState.numberFilterValues[filter.id] ?? null
  );

  return {
    type: 'number' as const,
    value,
    isDisabled: value === null,
    allowDecimals: true,
    label: popoverLabels.stringFilterLabel(filter.label.toLowerCase()),
    submitLabel: popoverLabels.numberSubmitLabel,
    setValue: (numberValue: number) => {
      commands.setNumberFilterValue(filter.id, numberValue);
    },
    onFormSubmit: () => {
      if (value === null) {
        return;
      }
      const filterExpression: NumberFilterExpression = {
        type: 'number',
        filterId: filter.id,
        operator: toInitialOperator(
          getTableFilterOperators('number'),
          filter,
          Operator.EQUAL
        ),
        selection: fromEditNumber(value, filter),
      };

      commands.createFilterExpressions(filter.id, filterExpression);
      commands.setMainPopoverOpen(false);
    },
  };
};

const toSearchedOptions = (
  filter: TableFilterList,
  searchString: string
): TableFilterListOption[] =>
  filter.remoteSearch
    ? filter.options
    : filter.options.filter(option =>
        option.label.toLowerCase().includes(searchString.toLowerCase())
      );

const toListContent = (
  filter: TableFilterList,
  optionCounts: Record<string, number | undefined>,
  internalState: TableFilterControlledState,
  commands: Commands<TableFilterList>,
  popoverLabels: TableFilterPopoverLabels = defaultTableFilterPopoverLabels
): ListPopoverProps => {
  const sessionExpression = internalState.sessionFilterExpressions.get(
    filter.id
  );
  const maybeCurrentFilterExpression =
    sessionExpression && sessionExpression.type === 'list'
      ? sessionExpression
      : null;
  const isCreatedInThisSession = sessionExpression !== undefined;

  const handleOptionToggle = (option: TableFilterListOption) => {
    const currentSelection = maybeCurrentFilterExpression?.selection ?? [];
    const isCurrentlySelected = currentSelection.some(
      selected => selected === option.id
    );

    const newSelection = isCurrentlySelected
      ? currentSelection.filter(selected => selected !== option.id)
      : [...currentSelection, option.id];

    if (isCreatedInThisSession && maybeCurrentFilterExpression) {
      const filterExpression: TableFilterExpression<TableFilterList> = {
        ...maybeCurrentFilterExpression,
        selection: newSelection,
      };
      commands.updateFilterExpression(filterExpression);
    } else {
      const filterExpression: ListFilterExpression = {
        type: 'list',
        filterId: filter.id,
        operator: toInitialOperator(
          getTableFilterOperators('list'),
          filter,
          Operator.CONTAINS
        ),
        selection: newSelection,
      };
      commands.createFilterExpressions(filter.id, filterExpression);
    }
  };

  return {
    type: 'list',
    loadingLabel: popoverLabels.loadingLabel,
    noResultsLabel: popoverLabels.noResultsLabel,
    filter: toListSearchProps(
      filter,
      internalState.listFilterSearchStrings[filter.id] ?? '',
      commands,
      popoverLabels
    ),
    groups: [
      {
        options: toSearchedOptions(
          filter,
          internalState.listFilterSearchStrings[filter.id] ?? ''
        ).map(option => {
          const isChecked =
            isCreatedInThisSession && maybeCurrentFilterExpression
              ? maybeCurrentFilterExpression.selection.some(
                  selected => selected === option.id
                )
              : false;

          return {
            id: option.id,
            label: option.label,
            icon: option.icon || null,
            rightIcon: null,
            count: optionCounts[option.id] || null,
            itemCountLabel: popoverLabels.itemCountLabel,
            isChecked,
            onClick: () => {
              handleOptionToggle(option);
              commands.setMainPopoverOpen(false);
            },
            onCheckboxClick: () => handleOptionToggle(option),
          };
        }),
      },
    ],
  };
};

const toBooleanContent = (
  filter: TableFilterBoolean,
  commands: Commands<TableFilterBoolean>
): BooleanPopoverProps => ({
  type: 'boolean',
  onClick: () => {
    const filterExpression: BooleanFilterExpression = {
      type: 'boolean',
      filterId: filter.id,
      operator: Operator.EQUAL,
    };
    commands.createFilterExpressions(filter.id, filterExpression);
    commands.setMainPopoverOpen(false);
  },
});
